import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, Target, Zap, Globe } from 'lucide-react';
import { whoIsItFor } from '../data/mock';

const AboutSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section ref={ref} className="section-padding">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - What is LayerSeek */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm text-[var(--accent-primary)] uppercase tracking-widest font-medium">What is LayerSeek</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6">
              The room where DeFi
              <span className="text-[var(--accent-primary)]"> deals happen.</span>
            </h2>
            <p className="text-[var(--text-secondary)] text-base mb-5 leading-relaxed">
              Every protocol needs the same things to grow: grants to extend runway and be recognized, integrations that unlock distribution, partners that co-market to achieve greater mindshare, infra that cuts months off your GTM, and the occasional acquirer, RFP/contract, joint venture proposal or accelerator that changes the trajectory of your protocol entirely.
            </p>
            <p className="text-[var(--text-secondary)] text-base mb-5 leading-relaxed">
              Today, most of that happens in scattered Telegram DMs, expensive IRL conferences, and cold outreach over a long period of time. The right person is always one intro away, and the intro rarely comes.
            </p>
            <p className="text-[var(--text-secondary)] text-base mb-8 leading-relaxed">
              LayerSeek is the room where it happens directly. One day. One virtual venue. Builders, chains, foundations, infra teams, and capital in the same space, ready to talk. AI matchmaking puts you in front of the people who want what you&apos;re building, and the people building what you need.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Users, label: "500+ Attendees" },
                { icon: Target, label: "B2B Focused" },
                { icon: Zap, label: "AI Matchmaking" },
                { icon: Globe, label: "Global Access" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-subtle)]">
                  <item.icon className="w-5 h-5 text-[var(--accent-primary)]" />
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* Right Column - Who Should Attend */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <span className="text-sm text-[var(--accent-primary)] uppercase tracking-widest font-medium">Who Should Attend</span>
            <h3 className="text-2xl md:text-3xl font-bold mt-4 mb-3">
              If you&apos;re building in DeFi,
              <span className="text-[var(--accent-primary)]"> you belong here.</span>
            </h3>
            
            <div className="space-y-3 mt-8">
              {whoIsItFor.map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-4 p-4 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] transition-colors cursor-default"
                >
                  <div className="w-10 h-10 rounded-lg bg-[var(--accent-bg)] flex items-center justify-center flex-shrink-0">
                    <span className="text-[var(--accent-primary)] font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm sm:text-base">{item.title}</div>
                    <div className="text-xs sm:text-sm text-[var(--text-muted)] mt-0.5 leading-relaxed">{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;